import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Printer, RefreshCw, FileText, AlertCircle, Info } from 'lucide-react';
import api from '../../utils/api';
import { isoDate } from '../../utils/time';
import { getBranding } from '../../utils/exportBranding';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { addDays } from 'date-fns';
import {
  PAGE, OverflowMenu, ErrorState, TableSkeleton, EmptyState, PersonAvatar, money, fmtDate, fmtTime, humanise, num, plural,
} from '../../components/ui';
import { employmentLabel, toDay } from '../Staff/hrKit';

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}

// Pay bands name a classification, not a state, so they are plain words.
const BAND_LABELS = {
  'weekday-day': 'Weekday day', 'weekday-early': 'Weekday early', 'weekday-evening': 'Weekday evening',
  'weekday-night': 'Weekday night', saturday: 'Saturday', sunday: 'Sunday', public_holiday: 'Public holiday',
};
function bandLabel(code) {
  if (!code) return 'Standard';
  if (BAND_LABELS[code]) return BAND_LABELS[code];
  for (const [k, v] of Object.entries(BAND_LABELS)) {
    if (code.toLowerCase().includes(k.replace('-', ''))) return v;
  }
  return humanise(code);
}

const dayLong = (v) => { const d = toDay(v); return d ? `${d.toLocaleDateString('en-AU', { weekday: 'short' })} ${fmtDate(d)}` : '—'; };
const periodText = (a, b) => {
  const s = toDay(a); const e = toDay(b);
  if (!s || !e) return '—';
  const wd = (d) => d.toLocaleDateString('en-AU', { weekday: 'short' });
  const sameYear = s.getFullYear() === e.getFullYear();
  return `${wd(s)} ${s.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })} – ${wd(e)} ${fmtDate(e)}`;
};
const maskTail = (v) => { const d = String(v || '').replace(/\s/g, ''); return d ? `${'•'.repeat(Math.max(0, d.length - 3))}${d.slice(-3)}` : ''; };

const PRINT_CSS = `
#payslip-print-root { display: none; }
@media print {
  body > *:not(#payslip-print-root) { display: none !important; }
  #payslip-print-root { display: block !important; }
  #payslip-print-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { margin: 14mm; }
}`;

function Row({ label, children, strong }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? 'font-semibold text-slate-900 tabular-nums' : 'text-slate-800 tabular-nums text-right'}>{children}</dd>
    </div>
  );
}

// The statement itself — drawn once for the screen and once, identically, for print.
function EarningsStatement({ payslip, branding, weekStart, weekEnd, onRecalculate, recalculating }) {
  const s = payslip.summary || {};
  const staff = payslip.staff || {};
  const employer = payslip.employer || {};
  const timesheets = payslip.timesheets || [];
  const isContractor = staff.employment_type === 'contractor';
  const name = `${staff.first_name || ''} ${staff.last_name || ''}`.trim();
  const logo = branding && branding.logoDataUrl;

  return (
    <article className="bg-white text-[13px] text-slate-800" aria-label={`Earnings statement for ${name}`}>
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 sm:px-8">
        <div className="flex min-w-0 items-center gap-3">
          {logo && <img src={logo} alt="" className="h-10 max-w-[140px] object-contain" />}
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900">{employer.company_name || (branding && branding.name) || 'Your organisation'}</p>
            {employer.abn && <p className="text-[12px] text-slate-500 tabular-nums">ABN {employer.abn}</p>}
            {employer.address && <p className="text-[12px] text-slate-500">{employer.address}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold tracking-[-0.01em] text-slate-900">Earnings statement</p>
          <p className="mt-0.5 text-[13px] text-slate-600">{periodText(weekStart, weekEnd)}</p>
          <p className="mt-0.5 text-[12px] text-slate-500">Prepared {dayLong(new Date())}</p>
        </div>
      </header>

      <div className="space-y-6 px-6 py-5 sm:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <section className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-3 text-[12px] font-semibold text-slate-500">Employee</h3>
            <div className="mb-3 flex items-center gap-3">
              <PersonAvatar id={staff.id} firstName={staff.first_name} lastName={staff.last_name} size="md" />
              <div>
                <p className="font-semibold text-slate-900">{name}</p>
                <p className="text-[12px] text-slate-500">{staff.employment_type ? employmentLabel(staff.employment_type) : 'Casual'}</p>
              </div>
            </div>
            <dl className="text-[12.5px]">
              {staff.email && <Row label="Email">{staff.email}</Row>}
              {staff.phone && <Row label="Phone">{staff.phone}</Row>}
              {staff.abn && <Row label="ABN">{staff.abn}</Row>}
              {staff.tfn && <Row label="TFN">{maskTail(staff.tfn)}</Row>}
              {(staff.bank_bsb || staff.bank_account_number) && (
                <>
                  {staff.bank_account_name && <Row label="Account name">{staff.bank_account_name}</Row>}
                  {staff.bank_bsb && <Row label="BSB">{staff.bank_bsb}</Row>}
                  {staff.bank_account_number && <Row label="Account">{maskTail(staff.bank_account_number)}</Row>}
                </>
              )}
              {staff.super_fund_name && <Row label="Super fund">{staff.super_fund_name}</Row>}
              {staff.super_member_number && <Row label="Member number">{staff.super_member_number}</Row>}
            </dl>
          </section>

          <section className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-3 text-[12px] font-semibold text-slate-500">Pay details</h3>
            <dl className="text-[12.5px]">
              {isContractor && (() => {
                const wk = parseFloat(staff.contractor_weekday_rate) || 0;
                const eff = (v) => { const n = parseFloat(v); return n > 0 ? n : wk; };
                return (
                  <>
                    {[['Weekday rate', wk], ['Saturday', eff(staff.contractor_saturday_rate)], ['Sunday', eff(staff.contractor_sunday_rate)], ['Public holiday', eff(staff.contractor_ph_rate)]].map(([lbl, r]) => (
                      <Row key={lbl} label={lbl}>{r > 0 ? `${money(r)}/hr` : '—'}</Row>
                    ))}
                    <Row label="Superannuation">{staff.contractor_super_applies ? 'Paid' : 'Not paid'}</Row>
                    <p className="pt-1 text-[11.5px] text-slate-500">Flat contractor rates. No SCHADS award loadings.</p>
                  </>
                );
              })()}
              {staff.pay_group_name && <Row label="Pay group">{staff.pay_group_name}</Row>}
              {staff.schads_level && <Row label="SCHADS level">{staff.schads_level}</Row>}
              {staff.override_hourly_rate ? <Row label="Custom rate">{money(staff.override_hourly_rate)}/hr</Row>
                : staff.base_hourly_rate ? <Row label="Base rate">{money(staff.base_hourly_rate)}/hr</Row> : null}
              {!staff.override_hourly_rate && staff.casual_loading_pct && <Row label="Casual loading">{num(staff.casual_loading_pct, 0)}%</Row>}
              <div className="mt-1 border-t border-slate-100 pt-1">
                <Row label="Employment basis">{staff.employment_type ? employmentLabel(staff.employment_type) : 'Casual'}</Row>
                <Row label="Period">{periodText(weekStart, weekEnd)}</Row>
              </div>
            </dl>
          </section>
        </div>

        <section>
          <h3 className="mb-2 text-[12px] font-semibold text-slate-500">Earnings by shift</h3>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[760px] text-[12.5px]">
              <thead>
                <tr className="border-b border-slate-200 bg-white text-left text-[12px] text-slate-500">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Shift</th>
                  <th className="px-3 py-2 font-medium">Pay band</th>
                  <th className="px-3 py-2 text-right font-medium">Hours</th>
                  <th className="px-3 py-2 text-right font-medium">Base rate</th>
                  <th className="px-3 py-2 text-right font-medium">Multiplier</th>
                  <th className="px-3 py-2 text-right font-medium">Effective rate</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.map((ts) => {
                  const hrs = parseFloat(ts.hours_worked || 0);
                  const pay = parseFloat(ts.pay_amount || 0);
                  const mult = parseFloat(ts.pay_multiplier || 1);
                  // Per-timesheet effective_base_rate (set by the server); falls back to the override or pay group rate.
                  const baseRate = parseFloat(ts.effective_base_rate || staff.override_hourly_rate || staff.base_hourly_rate || 0);
                  const effRate = pay > 0 && hrs > 0 ? pay / hrs : baseRate * mult;
                  const start = ts.actual_start || ts.start_time;
                  const end = ts.actual_end || ts.end_time;
                  return (
                    <tr key={ts.id} className="border-b border-slate-100 last:border-0">
                      <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-800">{dayLong(String(ts.shift_date).slice(0, 10))}</td>
                      <td className="px-3 py-2.5">
                        <p className="flex flex-wrap items-center gap-1.5 font-medium text-slate-800">
                          {ts.shift_type ? humanise(ts.shift_type) : 'Support shift'}
                          {['cancelled_with_charge', 'cancelled', 'declined'].includes(ts.shift_status) && <span className="badge-yellow">Cancellation pay</span>}
                        </p>
                        {ts.client_names && <p className="text-[12px] text-slate-500">{ts.client_names}</p>}
                        <p className="text-[12px] text-slate-500">{start ? fmtTime(start) : '—'} – {end ? fmtTime(end) : '—'}</p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{bandLabel(ts.pay_band)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{num(hrs, 2)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{baseRate > 0 ? money(baseRate) : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{mult > 0 ? `×${num(mult, 2)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{effRate > 0 ? `${money(effRate)}/hr` : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{pay > 0 ? money(pay) : <span className="font-normal text-slate-400">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={3} className="px-3 py-2.5 font-semibold text-slate-800">Subtotal, {plural(timesheets.length, 'shift')}</td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{num(s.totalHours || 0, 2)}</td>
                  <td colSpan={3} />
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{money(s.totalGrossPay || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {(s.mileageKm > 0 || s.totalExpenses > 0 || s.sleepovers > 0) && (
          <section>
            <h3 className="mb-2 text-[12px] font-semibold text-slate-500">Allowances and reimbursements</h3>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[520px] text-[12.5px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[12px] text-slate-500">
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Quantity</th>
                    <th className="px-3 py-2 text-right font-medium">Rate</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {s.mileageKm > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="px-3 py-2.5">Mileage allowance (ATO rate)</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{num(s.mileageKm, 1)} km</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(s.mileageRate)}/km</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{money(s.mileageAmount)}</td>
                    </tr>
                  )}
                  {s.totalExpenses > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="px-3 py-2.5">Expense reimbursement</td>
                      <td className="px-3 py-2.5 text-right text-slate-400">—</td>
                      <td className="px-3 py-2.5 text-right text-slate-400">—</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{money(s.totalExpenses)}</td>
                    </tr>
                  )}
                  {s.sleepovers > 0 && (
                    <tr>
                      <td className="px-3 py-2.5">Sleepover allowance (SCHADS 2025-26)</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{plural(s.sleepovers, 'night')}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(s.sleepoverRate)}/night</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{money(s.sleepoverAmount)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="flex justify-end">
          <dl className="w-full max-w-sm overflow-hidden rounded-lg border border-slate-200 text-[13px]">
            <div className="flex justify-between px-4 py-2"><dt className="text-slate-600">Gross wages</dt><dd className="font-medium tabular-nums">{money(s.totalGrossPay || 0)}</dd></div>
            {s.mileageAmount > 0 && <div className="flex justify-between px-4 py-2"><dt className="text-slate-600">Mileage allowance</dt><dd className="font-medium tabular-nums">{money(s.mileageAmount)}</dd></div>}
            {s.totalExpenses > 0 && <div className="flex justify-between px-4 py-2"><dt className="text-slate-600">Expenses</dt><dd className="font-medium tabular-nums">{money(s.totalExpenses)}</dd></div>}
            {s.sleepoverAmount > 0 && <div className="flex justify-between px-4 py-2"><dt className="text-slate-600">Sleepover allowance</dt><dd className="font-medium tabular-nums">{money(s.sleepoverAmount)}</dd></div>}
            <div className="flex items-baseline justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
              <dt className="font-semibold text-slate-900">Total payable</dt>
              <dd className="text-lg font-bold tabular-nums text-slate-900">{money(s.totalPayable || 0)}</dd>
            </div>
          </dl>
        </section>

        {s.totalGrossPay === 0 && onRecalculate && (
          <div className="flex flex-wrap items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 print:hidden">
            <AlertCircle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
            <p className="flex-1">
              Pay hasn't been calculated for these shifts. Set a pay group on <Link to={`/staff/${staff.id}/edit#pay`} className="font-medium underline">{staff.first_name || 'their'}'s profile</Link>, then recalculate pay.
            </p>
            <button type="button" onClick={onRecalculate} disabled={recalculating} className="btn-secondary btn-sm">
              <RefreshCw size={13} className={recalculating ? 'animate-spin' : ''} aria-hidden="true" />{recalculating ? 'Recalculating…' : 'Recalculate pay'}
            </button>
          </div>
        )}

        <footer className="border-t border-slate-200 pt-4 text-[11.5px] leading-relaxed text-slate-500">
          This earnings statement shows gross earnings and allowances from approved timesheets for the period. It does not show tax withheld, net pay, super contributions or year-to-date totals: your payroll system works those out and issues the payslip.
          {employer.contact_email && ` Questions? Contact ${employer.contact_email}.`}
        </footer>
      </div>
    </article>
  );
}

export default function Payslips() {
  const confirm = useConfirm();
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [weekStart, setWeekStart] = useState(() => {
    const d = getMonday(new Date());
    d.setDate(d.getDate() - 7); // default to the previous week
    return isoDate(d);
  });
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [staffError, setStaffError] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [branding, setBranding] = useState(null);
  const [printing, setPrinting] = useState(false);

  const weekEnd = isoDate(addDays(toDay(weekStart), 6));

  const loadStaff = useCallback(() => {
    setLoadingStaff(true); setStaffError(null);
    api.get('/staff')
      .then((r) => {
        const list = r.data?.staff || r.data || [];
        setStaffList(list);
        setStaffId((cur) => cur || (list[0] && list[0].id) || '');
      })
      .catch((e) => setStaffError(e.response?.data?.message || "We couldn't load your staff. Try again."))
      .finally(() => setLoadingStaff(false));
  }, []);
  useEffect(() => { loadStaff(); getBranding().then(setBranding).catch(() => {}); }, [loadStaff]);

  const load = useCallback(() => {
    if (!staffId) return;
    setLoading(true); setLoadError(null); setPayslip(null);
    api.get(`/timesheets/payslip?staffId=${staffId}&weekStart=${weekStart}&weekEnd=${weekEnd}`)
      .then((r) => setPayslip(r.data))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load this earnings statement. Try again."))
      .finally(() => setLoading(false));
  }, [staffId, weekStart, weekEnd]);
  useEffect(() => { load(); }, [load]);

  const staffNameOf = (id) => { const s = staffList.find((x) => x.id === id); return s ? `${s.first_name} ${s.last_name}` : 'this staff member'; };
  const timesheets = (payslip && payslip.timesheets) || [];

  const recalculate = async () => {
    if (!staffId) return;
    const n = timesheets.length;
    const ok = await confirm(
      `Pay on ${plural(n, 'approved timesheet')} will be worked out again from ${staffNameOf(staffId)}'s current pay group or rate, and the stored amounts replaced.`,
      { title: `Recalculate pay for ${staffNameOf(staffId)}, ${periodText(weekStart, weekEnd)}?`, confirmLabel: 'Recalculate pay' },
    );
    if (!ok) return;
    setRecalculating(true);
    try {
      const r = await api.post('/timesheets/recalculate-pay', { staffId, weekStart, weekEnd });
      toast.success(r.data?.message || 'Pay recalculated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Pay wasn't recalculated. Try again.");
    } finally {
      setRecalculating(false);
    }
  };

  const shiftWeek = (n) => {
    const d = toDay(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(isoDate(d));
  };

  // Print the same component with the app hidden, so the PDF matches the screen.
  useEffect(() => {
    if (!printing) return undefined;
    const prevTitle = document.title;
    const s = payslip && payslip.staff;
    document.title = `Earnings statement — ${s ? `${s.first_name} ${s.last_name}` : ''} — ${weekStart}`;
    const done = () => { setPrinting(false); document.title = prevTitle; };
    window.addEventListener('afterprint', done);
    const t = setTimeout(() => { window.print(); }, 60);
    return () => { clearTimeout(t); window.removeEventListener('afterprint', done); document.title = prevTitle; };
  }, [printing, payslip, weekStart]);

  const s = (payslip && payslip.summary) || {};
  const hasStatement = !loading && !loadError && payslip && timesheets.length > 0;

  return (
    <div className={PAGE}>
      <style>{PRINT_CSS}</style>

      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 md:flex-row md:flex-wrap md:items-end">
        <div className="flex flex-col gap-1">
          <label htmlFor="payslip-staff" className="text-[13px] font-medium text-slate-700">Staff member</label>
          {loadingStaff ? <div className="skeleton h-9 w-56" />
            : staffError ? <span className="text-[13px] text-rose-700">{staffError} <button type="button" onClick={loadStaff} className="font-medium underline">Try again</button></span>
              : (
                <select id="payslip-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)} className="h-9 min-w-[220px] rounded-lg border border-slate-200 bg-white px-3 text-sm">
                  {staffList.map((st) => <option key={st.id} value={st.id}>{st.first_name} {st.last_name}</option>)}
                </select>
              )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-slate-700" id="payslip-week">Pay week</span>
          <div className="flex items-center gap-1" role="group" aria-labelledby="payslip-week">
            <button type="button" onClick={() => shiftWeek(-1)} aria-label="Previous week" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronLeft size={15} aria-hidden="true" /></button>
            <div className="flex h-9 min-w-[220px] items-center justify-center rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-800">{periodText(weekStart, weekEnd)}</div>
            <button type="button" onClick={() => shiftWeek(1)} aria-label="Next week" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronRight size={15} aria-hidden="true" /></button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:ml-auto">
          {hasStatement && (
            <div className="text-right">
              <p className="text-[12px] text-slate-500">Total payable</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">{money(s.totalPayable || 0)}</p>
            </div>
          )}
          {hasStatement && <button type="button" onClick={() => setPrinting(true)} className="btn-primary btn-sm"><Printer size={14} aria-hidden="true" />Print or save PDF</button>}
          {payslip && !loading && (
            <OverflowMenu label="More pay actions" items={[
              { label: recalculating ? 'Recalculating…' : 'Recalculate pay…', icon: RefreshCw, onClick: recalculate, disabled: recalculating || !timesheets.length },
            ]} />
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading && <TableSkeleton rows={6} cols={5} />}
        {!loading && loadError && <ErrorState message={loadError} onRetry={load} />}
        {!loading && !loadError && !staffId && !loadingStaff && (
          <EmptyState icon={FileText} title="No staff to show" hint="Add staff members, then their approved timesheets appear here as earnings statements." />
        )}
        {!loading && !loadError && payslip && timesheets.length === 0 && (
          <EmptyState icon={FileText} title="No approved timesheets for this week"
            hint="Timesheets must be approved before they appear on an earnings statement."
            action={<Link to="/timesheets" className="btn-secondary btn-sm">Go to timesheets</Link>} />
        )}
        {hasStatement && (
          <EarningsStatement payslip={payslip} branding={branding} weekStart={weekStart} weekEnd={weekEnd} onRecalculate={recalculate} recalculating={recalculating} />
        )}
      </div>

      {hasStatement && (
        <p className="flex items-start gap-2 text-[12.5px] text-slate-500">
          <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          Amounts are the pay stored when each timesheet was approved. Where a shift has no stored pay, the figure shown is worked out from the current rate and isn't saved.
        </p>
      )}

      {printing && hasStatement && createPortal(
        <div id="payslip-print-root">
          <EarningsStatement payslip={payslip} branding={branding} weekStart={weekStart} weekEnd={weekEnd} />
        </div>,
        document.body,
      )}
    </div>
  );
}
